import React, { memo } from 'react';
import { Tooltip } from '@mui/material';
import './AgentProgress.css';

/**
 * Step status icons
 */
const StatusIcon = ({ status }) => {
    switch (status) {
        case 'completed':
            return (
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" className="status-icon completed">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
            );
        case 'running':
            return (
                <svg viewBox="0 0 24 24" width="16" height="16" className="status-icon running">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" strokeDasharray="31.4" strokeDashoffset="10" />
                </svg>
            );
        case 'failed':
            return (
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" className="status-icon failed">
                    <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"/>
                </svg>
            );
        case 'pending':
        default:
            return (
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" className="status-icon pending">
                    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.3"/>
                </svg>
            );
    }
};

/**
 * AgentProgress component for displaying multi-step task progress
 * Shows a vertical timeline of steps with status indicators
 */
const AgentProgress = memo(({
    steps,
    title = "Processing"
}) => {
    if (!steps || steps.length === 0) {
        return null;
    }

    // Calculate overall progress
    const completedSteps = steps.filter(s => s.status === 'completed').length;
    const totalSteps = steps.length;
    const progressPercent = Math.round((completedSteps / totalSteps) * 100);

    // Check if any step is running
    const hasRunningStep = steps.some(s => s.status === 'running');
    const hasFailed = steps.some(s => s.status === 'failed');
    const isComplete = completedSteps === totalSteps && !hasFailed;

    return (
        <div className={`ntb-agent-progress ${isComplete ? 'complete' : ''} ${hasFailed ? 'has-failed' : ''}`}>
            <div className="ntb-agent-progress-header">
                <div className="ntb-agent-progress-title">
                    {hasRunningStep && (
                        <svg className="ntb-progress-spinner" viewBox="0 0 24 24" width="16" height="16">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" strokeDasharray="31.4" strokeDashoffset="10" />
                        </svg>
                    )}
                    {isComplete && (
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" className="ntb-progress-complete-icon">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                    )}
                    {hasFailed && !hasRunningStep && (
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" className="ntb-progress-failed-icon">
                            <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"/>
                        </svg>
                    )}
                    <span>{title}</span>
                </div>
                <div className="ntb-agent-progress-counter">
                    {completedSteps}/{totalSteps} steps
                </div>
            </div>

            <div className="ntb-agent-progress-bar">
                <div
                    className="ntb-agent-progress-bar-fill"
                    style={{ width: `${progressPercent}%` }}
                />
            </div>

            <div className="ntb-agent-progress-steps">
                {steps.map((step, index) => (
                    <div
                        key={step.id || index}
                        className={`ntb-agent-step ${step.status}`}
                    >
                        <div className="ntb-step-connector">
                            {index > 0 && <div className="ntb-step-line" />}
                        </div>
                        <div className="ntb-step-indicator">
                            <StatusIcon status={step.status} />
                        </div>
                        <div className="ntb-step-content">
                            <div className="ntb-step-label">{step.label}</div>
                            {step.details && (
                                <Tooltip title={step.details} arrow placement="right">
                                    <div className="ntb-step-details">{step.details}</div>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
});

AgentProgress.displayName = 'AgentProgress';

export default AgentProgress;
